import { useState, type FormEvent, type ReactNode } from "react";
import {
  Loader2,
  LogIn,
  Mail,
  KeyRound,
  UserPlus,
  ArrowLeft,
  Shield,
} from "lucide-react";
import { Button } from "./ui/button.js";
import { Input } from "./ui/forms.js";
import {
  Card,
  CardContent,
  CardDescription,
  CardHeader,
  CardTitle,
} from "./ui/card.js";
import {
  signUp,
  confirmSignUp,
  resendConfirmationCode,
  signIn,
  completeNewPassword,
  forgotPassword,
  confirmForgotPassword,
  friendlyCognitoError,
} from "../lib/cognitoClient.js";
import type { CognitoUser } from "amazon-cognito-identity-js";

type Screen =
  | { kind: "signIn" }
  | { kind: "signUp" }
  | { kind: "confirmSignUp"; email: string; password?: string }
  | {
      kind: "newPasswordRequired";
      email: string;
      user: CognitoUser;
      userAttributes: Record<string, string>;
    }
  | { kind: "forgotPassword" }
  | { kind: "resetPassword"; email: string };

interface AuthShellProps {
  onAuthenticated: () => void;
}

const PASSWORD_HINT = "Min 8 characters, with upper, lower, and a number.";

function passwordOk(pw: string): boolean {
  return pw.length >= 8 && /[a-z]/.test(pw) && /[A-Z]/.test(pw) && /\d/.test(pw);
}

export function AuthShell({ onAuthenticated }: AuthShellProps) {
  const [screen, setScreen] = useState<Screen>({ kind: "signIn" });

  return (
    <div className="flex min-h-screen items-center justify-center bg-gradient-to-br from-slate-50 via-white to-slate-100 p-4">
      <Card className="w-full max-w-md border-slate-200 shadow-lg">
        <CardHeader className="text-center pb-2">
          <div className="mx-auto mb-4 flex h-14 w-14 items-center justify-center rounded-full bg-slate-900">
            <Shield className="h-7 w-7 text-white" />
          </div>
          <CardTitle className="text-2xl font-bold text-slate-900">
            D&amp;P OneNote Assistant
          </CardTitle>
          <CardDescription className="text-slate-500">
            {screen.kind === "signIn" && "Sign in to your account"}
            {screen.kind === "signUp" && "Create a new account"}
            {screen.kind === "confirmSignUp" && "Confirm your email"}
            {screen.kind === "newPasswordRequired" && "Set a new password"}
            {screen.kind === "forgotPassword" && "Reset your password"}
            {screen.kind === "resetPassword" && "Choose a new password"}
          </CardDescription>
        </CardHeader>
        <CardContent className="space-y-4 pt-4">
          {screen.kind === "signIn" && (
            <SignInForm
              onSuccess={onAuthenticated}
              onNewPasswordRequired={(email, user, attrs) =>
                setScreen({ kind: "newPasswordRequired", email, user, userAttributes: attrs })
              }
              onUnconfirmed={(email) =>
                setScreen({ kind: "confirmSignUp", email })
              }
              onGoSignUp={() => setScreen({ kind: "signUp" })}
              onGoForgot={() => setScreen({ kind: "forgotPassword" })}
            />
          )}
          {screen.kind === "signUp" && (
            <SignUpForm
              onSuccess={(email, password) =>
                setScreen({ kind: "confirmSignUp", email, password })
              }
              onGoSignIn={() => setScreen({ kind: "signIn" })}
            />
          )}
          {screen.kind === "confirmSignUp" && (
            <ConfirmSignUpForm
              email={screen.email}
              autoPassword={screen.password}
              onConfirmed={onAuthenticated}
              onGoSignIn={() => setScreen({ kind: "signIn" })}
            />
          )}
          {screen.kind === "newPasswordRequired" && (
            <NewPasswordForm
              user={screen.user}
              userAttributes={screen.userAttributes}
              onSuccess={onAuthenticated}
            />
          )}
          {screen.kind === "forgotPassword" && (
            <ForgotPasswordForm
              onCodeSent={(email) => setScreen({ kind: "resetPassword", email })}
              onGoSignIn={() => setScreen({ kind: "signIn" })}
            />
          )}
          {screen.kind === "resetPassword" && (
            <ResetPasswordForm
              email={screen.email}
              onReset={() => setScreen({ kind: "signIn" })}
              onGoSignIn={() => setScreen({ kind: "signIn" })}
            />
          )}
        </CardContent>
      </Card>
    </div>
  );
}

// ---------------- Sub-forms ----------------

function FieldError({ children }: { children: ReactNode | null }) {
  if (!children) return null;
  return <p className="text-xs text-red-600">{children}</p>;
}

function SubmitButton({
  busy,
  children,
}: {
  busy: boolean;
  children: ReactNode;
}) {
  return (
    <Button type="submit" disabled={busy} className="w-full bg-slate-900 hover:bg-slate-800 text-white h-11">
      {busy ? <Loader2 className="mr-2 h-4 w-4 animate-spin" /> : null}
      {children}
    </Button>
  );
}

function SignInForm(props: {
  onSuccess: () => void;
  onNewPasswordRequired: (
    email: string,
    user: CognitoUser,
    userAttributes: Record<string, string>,
  ) => void;
  onUnconfirmed: (email: string) => void;
  onGoSignUp: () => void;
  onGoForgot: () => void;
}) {
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const submit = async (e: FormEvent) => {
    e.preventDefault();
    setBusy(true);
    setError(null);
    try {
      const result = await signIn(email, password);
      if (result.status === "ok") {
        props.onSuccess();
      } else if (result.status === "new_password_required") {
        props.onNewPasswordRequired(email, result.user, result.userAttributes);
      }
    } catch (err) {
      const e = err as { code?: string; name?: string };
      if (e.code === "UserNotConfirmedException" || e.name === "UserNotConfirmedException") {
        props.onUnconfirmed(email);
        return;
      }
      setError(friendlyCognitoError(err));
    } finally {
      setBusy(false);
    }
  };

  return (
    <form onSubmit={submit} className="space-y-3">
      <Input
        type="email"
        value={email}
        onChange={(e) => setEmail(e.target.value)}
        placeholder="Email"
        required
        autoFocus
      />
      <Input
        type="password"
        value={password}
        onChange={(e) => setPassword(e.target.value)}
        placeholder="Password"
        required
      />
      <FieldError>{error}</FieldError>
      <SubmitButton busy={busy}>
        <LogIn className="mr-2 h-4 w-4" />
        Sign in
      </SubmitButton>
      <div className="flex items-center justify-between pt-1 text-xs">
        <button
          type="button"
          onClick={props.onGoSignUp}
          className="flex items-center gap-1 text-slate-600 hover:text-slate-900"
        >
          <UserPlus className="h-3.5 w-3.5" />
          Create an account
        </button>
        <button
          type="button"
          onClick={props.onGoForgot}
          className="flex items-center gap-1 text-slate-600 hover:text-slate-900"
        >
          <KeyRound className="h-3.5 w-3.5" />
          Forgot password?
        </button>
      </div>
    </form>
  );
}

function SignUpForm(props: {
  onSuccess: (email: string, password: string) => void;
  onGoSignIn: () => void;
}) {
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [confirm, setConfirm] = useState("");
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const submit = async (e: FormEvent) => {
    e.preventDefault();
    setError(null);
    if (password !== confirm) {
      setError("Passwords do not match.");
      return;
    }
    if (!passwordOk(password)) {
      setError(PASSWORD_HINT);
      return;
    }
    setBusy(true);
    try {
      await signUp(email, password);
      props.onSuccess(email, password);
    } catch (err) {
      setError(friendlyCognitoError(err));
    } finally {
      setBusy(false);
    }
  };

  return (
    <form onSubmit={submit} className="space-y-3">
      <Input
        type="email"
        value={email}
        onChange={(e) => setEmail(e.target.value)}
        placeholder="Email"
        required
        autoFocus
      />
      <Input
        type="password"
        value={password}
        onChange={(e) => setPassword(e.target.value)}
        placeholder="Password"
        required
      />
      <Input
        type="password"
        value={confirm}
        onChange={(e) => setConfirm(e.target.value)}
        placeholder="Confirm password"
        required
      />
      <p className="text-xs text-slate-400">{PASSWORD_HINT}</p>
      <FieldError>{error}</FieldError>
      <SubmitButton busy={busy}>
        <UserPlus className="mr-2 h-4 w-4" />
        Create account
      </SubmitButton>
      <button
        type="button"
        onClick={props.onGoSignIn}
        className="flex w-full items-center justify-center gap-1 text-xs text-slate-600 hover:text-slate-900"
      >
        <ArrowLeft className="h-3.5 w-3.5" />
        Already have an account? Sign in
      </button>
    </form>
  );
}

function ConfirmSignUpForm(props: {
  email: string;
  autoPassword?: string;
  onConfirmed: () => void;
  onGoSignIn: () => void;
}) {
  const [code, setCode] = useState("");
  const [busy, setBusy] = useState(false);
  const [resent, setResent] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const submit = async (e: FormEvent) => {
    e.preventDefault();
    setBusy(true);
    setError(null);
    try {
      await confirmSignUp(props.email, code.trim());
      if (props.autoPassword) {
        // Best-effort auto-sign-in so the newly-confirmed user doesn't
        // have to type their password again right after signing up.
        try {
          const r = await signIn(props.email, props.autoPassword);
          if (r.status === "ok") {
            props.onConfirmed();
            return;
          }
        } catch {
          /* fall through to sign-in screen */
        }
      }
      props.onGoSignIn();
    } catch (err) {
      setError(friendlyCognitoError(err));
    } finally {
      setBusy(false);
    }
  };

  const resend = async () => {
    try {
      await resendConfirmationCode(props.email);
      setResent(true);
      setError(null);
    } catch (err) {
      setError(friendlyCognitoError(err));
    }
  };

  return (
    <form onSubmit={submit} className="space-y-3">
      <p className="text-xs text-slate-500">
        <Mail className="mr-1 inline h-3 w-3" />
        We sent a 6-digit code to <strong>{props.email}</strong>.
      </p>
      <Input
        inputMode="numeric"
        value={code}
        onChange={(e) => setCode(e.target.value)}
        placeholder="Verification code"
        required
        autoFocus
      />
      <FieldError>{error}</FieldError>
      <SubmitButton busy={busy}>Confirm</SubmitButton>
      <div className="flex items-center justify-between text-xs">
        <button
          type="button"
          onClick={resend}
          disabled={resent}
          className="text-slate-600 hover:text-slate-900 disabled:opacity-60"
        >
          {resent ? "Code resent" : "Resend code"}
        </button>
        <button
          type="button"
          onClick={props.onGoSignIn}
          className="flex items-center gap-1 text-slate-600 hover:text-slate-900"
        >
          <ArrowLeft className="h-3.5 w-3.5" />
          Back to sign in
        </button>
      </div>
    </form>
  );
}

function NewPasswordForm(props: {
  user: CognitoUser;
  userAttributes: Record<string, string>;
  onSuccess: () => void;
}) {
  const [password, setPassword] = useState("");
  const [confirm, setConfirm] = useState("");
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const submit = async (e: FormEvent) => {
    e.preventDefault();
    setError(null);
    if (password !== confirm) {
      setError("Passwords do not match.");
      return;
    }
    if (!passwordOk(password)) {
      setError(PASSWORD_HINT);
      return;
    }
    setBusy(true);
    try {
      await completeNewPassword(props.user, password, props.userAttributes);
      props.onSuccess();
    } catch (err) {
      setError(friendlyCognitoError(err));
    } finally {
      setBusy(false);
    }
  };

  return (
    <form onSubmit={submit} className="space-y-3">
      <p className="text-xs text-slate-500">
        Your administrator gave you a temporary password. Pick a permanent one now.
      </p>
      <Input
        type="password"
        value={password}
        onChange={(e) => setPassword(e.target.value)}
        placeholder="New password"
        required
        autoFocus
      />
      <Input
        type="password"
        value={confirm}
        onChange={(e) => setConfirm(e.target.value)}
        placeholder="Confirm new password"
        required
      />
      <p className="text-xs text-slate-400">{PASSWORD_HINT}</p>
      <FieldError>{error}</FieldError>
      <SubmitButton busy={busy}>
        <KeyRound className="mr-2 h-4 w-4" />
        Set password
      </SubmitButton>
    </form>
  );
}

function ForgotPasswordForm(props: {
  onCodeSent: (email: string) => void;
  onGoSignIn: () => void;
}) {
  const [email, setEmail] = useState("");
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const submit = async (e: FormEvent) => {
    e.preventDefault();
    setBusy(true);
    setError(null);
    try {
      await forgotPassword(email);
      props.onCodeSent(email);
    } catch (err) {
      setError(friendlyCognitoError(err));
    } finally {
      setBusy(false);
    }
  };

  return (
    <form onSubmit={submit} className="space-y-3">
      <p className="text-xs text-slate-500">
        Enter your email and we'll send you a code to reset your password.
      </p>
      <Input
        type="email"
        value={email}
        onChange={(e) => setEmail(e.target.value)}
        placeholder="Email"
        required
        autoFocus
      />
      <FieldError>{error}</FieldError>
      <SubmitButton busy={busy}>
        <Mail className="mr-2 h-4 w-4" />
        Send reset code
      </SubmitButton>
      <button
        type="button"
        onClick={props.onGoSignIn}
        className="flex w-full items-center justify-center gap-1 text-xs text-slate-600 hover:text-slate-900"
      >
        <ArrowLeft className="h-3.5 w-3.5" />
        Back to sign in
      </button>
    </form>
  );
}

function ResetPasswordForm(props: {
  email: string;
  onReset: () => void;
  onGoSignIn: () => void;
}) {
  const [code, setCode] = useState("");
  const [password, setPassword] = useState("");
  const [confirm, setConfirm] = useState("");
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const submit = async (e: FormEvent) => {
    e.preventDefault();
    setError(null);
    if (password !== confirm) {
      setError("Passwords do not match.");
      return;
    }
    if (!passwordOk(password)) {
      setError(PASSWORD_HINT);
      return;
    }
    setBusy(true);
    try {
      await confirmForgotPassword(props.email, code.trim(), password);
      props.onReset();
    } catch (err) {
      setError(friendlyCognitoError(err));
    } finally {
      setBusy(false);
    }
  };

  return (
    <form onSubmit={submit} className="space-y-3">
      <p className="text-xs text-slate-500">
        We sent a code to <strong>{props.email}</strong>. Enter it with your new password.
      </p>
      <Input
        inputMode="numeric"
        value={code}
        onChange={(e) => setCode(e.target.value)}
        placeholder="Verification code"
        required
        autoFocus
      />
      <Input
        type="password"
        value={password}
        onChange={(e) => setPassword(e.target.value)}
        placeholder="New password"
        required
      />
      <Input
        type="password"
        value={confirm}
        onChange={(e) => setConfirm(e.target.value)}
        placeholder="Confirm new password"
        required
      />
      <p className="text-xs text-slate-400">{PASSWORD_HINT}</p>
      <FieldError>{error}</FieldError>
      <SubmitButton busy={busy}>Reset password</SubmitButton>
      <button
        type="button"
        onClick={props.onGoSignIn}
        className="flex w-full items-center justify-center gap-1 text-xs text-slate-600 hover:text-slate-900"
      >
        <ArrowLeft className="h-3.5 w-3.5" />
        Back to sign in
      </button>
    </form>
  );
}
